import { create } from 'zustand';
import { FormStep, ParentFormData, AddressFormData, ChildFormData } from '../types/leadForm.types';

interface FormProgress {
  currentStep: FormStep;
  completedSteps: FormStep[];
  totalSteps: number;
  currentStepIndex: number;
}

interface ScheduleData {
  type?: string;
  date?: string;
  time?: string;
  timeSlot?: string;
  duration?: number;
  location?: string;
  notes?: string;
}

interface DocumentsData {
  interestForm: File[];
  screenshot: File[];
  other: File[];
}

interface LeadFormData {
  parent: ParentFormData;
  address: AddressFormData;
  children: ChildFormData[];
  schedule: ScheduleData | null;
  documents: DocumentsData;
  notes: string;
  quality: number;
  // Primary school segment (campus) for this lead, when the project has schools.
  schoolSegmentId?: string;
  // Pre-set from the canvass flow when a lead is captured in person. If set,
  // ReviewStep uses these instead of the default uncontacted/pending.
  initialStatus?: string;
  initialDetermination?: string;
}

interface LeadFormStore {
  projectId: string | null;
  setProjectId: (projectId: string) => void;
  formData: LeadFormData;
  progress: FormProgress;
  errors: Record<string, string>;
  isSubmitting: boolean;

  setParentData: (data: Partial<ParentFormData>) => void;
  setAddressData: (data: Partial<AddressFormData>) => void;
  setChildrenData: (children: ChildFormData[]) => void;
  addChild: (child: ChildFormData) => void;
  removeChild: (index: number) => void;
  setScheduleData: (data: ScheduleData | null) => void;
  setDocumentData: (data: Partial<DocumentsData>) => void;
  setNotes: (notes: string) => void;
  setQuality: (quality: number) => void;
  updateFormData: (data: Partial<LeadFormData>) => void;
  setFormData: (data: LeadFormData) => void;

  goToStep: (step: FormStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  markStepCompleted: (step: FormStep) => void;
  setStep: (step: number) => void;

  setError: (field: string, message: string) => void;
  clearError: (field: string) => void;
  clearErrors: () => void;

  saveToStorage: () => void;
  loadFromStorage: () => void;
  resetForm: () => void;
}

const STORAGE_KEY = 'canvassed_lead_form';

const steps: FormStep[] = ['parent', 'address', 'children', 'schedule', 'documents', 'review'];

const initialFormData = {
  parent: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredContact: 'phone'
  },
  address: {
    street: '',
    city: '',
    state: '',
    zipCode: ''
  },
  children: [],
  schedule: null,
  documents: {
    interestForm: [],
    screenshot: [],
    other: []
  },
  notes: '',
  quality: 3
};

const initialProgress: FormProgress = {
  currentStep: 'parent',
  completedSteps: [],
  totalSteps: steps.length,
  currentStepIndex: 0
};

export const useLeadFormStore = create<LeadFormStore>((set, get) => ({
  projectId: null,
  formData: initialFormData,
  progress: initialProgress,
  errors: {},
  isSubmitting: false,
  
  setParentData: (data) =>
    set((state) => ({
      formData: {
        ...state.formData,
        parent: { ...state.formData.parent, ...data }
      }
    })),
    
  setAddressData: (data) =>
    set((state) => ({
      formData: {
        ...state.formData,
        address: { ...state.formData.address, ...data }
      }
    })),
    
  setChildrenData: (children) =>
    set((state) => ({
      formData: { ...state.formData, children }
    })),
    
  addChild: (child) =>
    set((state) => ({
      formData: {
        ...state.formData,
        children: [...state.formData.children, child]
      }
    })),
    
  removeChild: (index) =>
    set((state) => ({
      formData: {
        ...state.formData,
        children: state.formData.children.filter((_, i) => i !== index)
      }
    })),
    
  setScheduleData: (data) =>
    set((state) => ({
      formData: {
        ...state.formData,
        schedule: data ? { ...state.formData.schedule, ...data } : null
      }
    })),
    
  setDocumentData: (data) =>
    set((state) => ({
      formData: {
        ...state.formData,
        documents: { ...state.formData.documents, ...data }
      }
    })),
    
  setNotes: (notes) =>
    set((state) => ({
      formData: { ...state.formData, notes }
    })),
    
  setQuality: (quality) =>
    set((state) => ({
      formData: { ...state.formData, quality }
    })),
  
    setProjectId: (projectId) => set({ projectId }), 
    
  updateFormData: (data) =>
    set((state) => ({
      formData: { ...state.formData, ...data }
    })),
    
  setFormData: (data) =>
    set(() => ({
      formData: data
    })),
  
  goToStep: (step) => {
    const stepIndex = steps.indexOf(step);
    set((state) => ({
      progress: {
        ...state.progress,
        currentStep: step,
        currentStepIndex: stepIndex
      }
    }));
  },
  
  nextStep: () => {
    const { progress } = get();
    const nextIndex = Math.min(progress.currentStepIndex + 1, steps.length - 1);
    const nextStep = steps[nextIndex];
    
    set((state) => ({
      progress: {
        ...state.progress,
        currentStep: nextStep,
        currentStepIndex: nextIndex
      }
    }));
  },
  
  previousStep: () => {
    const { progress } = get();
    const prevIndex = Math.max(progress.currentStepIndex - 1, 0);
    const prevStep = steps[prevIndex];
    
    set((state) => ({
      progress: {
        ...state.progress,
        currentStep: prevStep,
        currentStepIndex: prevIndex
      }
    }));
  },
  
  markStepCompleted: (step) => {
    set((state) => ({
      progress: {
        ...state.progress,
        completedSteps: state.progress.completedSteps.includes(step)
          ? state.progress.completedSteps
          : [...state.progress.completedSteps, step]
      }
    }));
  },
  
  setStep: (stepIndex) => {
    const step = steps[stepIndex] || steps[0];
    set((state) => ({
      progress: {
        ...state.progress,
        currentStep: step,
        currentStepIndex: stepIndex
      }
    }));
  },
  
  setError: (field, message) =>
    set((state) => ({
      errors: { ...state.errors, [field]: message }
    })),
    
  clearError: (field) =>
    set((state) => {
      const { [field]: _, ...rest } = state.errors;
      return { errors: rest };
    }),
    
  clearErrors: () =>
    set(() => ({ errors: {} })),
  
  saveToStorage: () => {
    const state = get();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        formData: state.formData,
        progress: state.progress,
        projectId: state.projectId
      }));
    } catch (error) {
      console.error('Failed to save form data:', error);
    }
  },
  
  loadFromStorage: () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { formData, progress, projectId } = JSON.parse(saved);
        set({ formData, progress, projectId: projectId || null });
      }
    } catch (error) {
      console.error('Failed to load form data:', error);
    }
  },
  
  resetForm: () => {
    localStorage.removeItem(STORAGE_KEY);
    set({
      formData: initialFormData,
      progress: initialProgress,
      errors: {},
      isSubmitting: false,
      projectId: null
    });
  }
}));
